/**
 * 报名编辑对话框（组级，enrollment-submission-source-alignment:SC-39~SC-41）。
 *
 * 顶部只读摘要（学生/校区/课程SKU/提交时间）；编辑归组：来源/顾问/科目分数/备注一次
 * 作用于整张报名单。科目分数按该报名 SKU 涉及科目逐科一行，预填取学生当前该科分数
 * （student_subject_scores，subject-score-bands-and-scores）；来源读系统设置。
 */
import { useEffect, useState } from 'react';
import { Alert, Descriptions, Form, Input, InputNumber, Select, message } from 'antd';
import { DraggableModal } from '../../components/DraggableModal';
import {
  enrollmentsApi,
  type EnrollmentItem,
} from '../../api/enrollments.api';
import { staffApi, type StaffListItem } from '../../api/staff.api';
import { courseSkuApi } from '../../api/course-skus.api';
import { subjectsApi } from '../../api/subjects.api';
import { studentsApi } from '../../api/students.api';
import { systemSettingsApi, type EnrollmentSourceItem } from '../../api/system-settings.api';
import { fmtDateTime } from '../../utils/datetime';

export interface EnrollmentEditModalProps {
  open: boolean;
  item: EnrollmentItem;
  onClose: () => void;
  onSaved: () => void;
}

const FALLBACK_SOURCES: EnrollmentSourceItem[] = [
  { value: 'STAFF_ASSISTED', label: '员工代建' },
  { value: 'PARENT_SELF', label: '家长自助' },
  { value: 'SHARE_REFERRAL', label: '好友分享' },
];

/** 该报名 SKU 涉及科目的展示与提交口径（单条视角：一次编辑一个 SKU 科目）。 */
interface SkuSubjectContext {
  subjectId: string | null;
  subjectName: string;
  fullScore: number;
}

export function EnrollmentEditModal({ open, item, onClose, onSaved }: EnrollmentEditModalProps) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [staffs, setStaffs] = useState<StaffListItem[]>([]);
  const [sources, setSources] = useState<EnrollmentSourceItem[]>(FALLBACK_SOURCES);
  const [skuSubject, setSkuSubject] = useState<SkuSubjectContext>({
    subjectId: null,
    subjectName: '该科目',
    fullScore: 100,
  });

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        source: item.source,
        consultantId: item.consultantId ?? undefined,
        score: null,
        note: item.note ?? '',
      });
      staffApi
        .listStaffs({ page: 1, pageSize: 100 })
        .then((r) => setStaffs(r.items))
        .catch(() => setStaffs([]));
      systemSettingsApi
        .getSettings()
        .then((s) => {
          if (s.enrollmentSources?.length) setSources(s.enrollmentSources);
        })
        .catch(() => setSources(FALLBACK_SOURCES));
      void Promise.all([
        subjectsApi.listSubjects({ page: 1, pageSize: 100 }),
        courseSkuApi.listCourseSkus({ page: 1, pageSize: 100 }),
        studentsApi.getStudent(item.studentId).catch(() => null),
      ]).then(([subjectPage, skuPage, student]) => {
        const sku = skuPage.items.find((x) => x.id === item.skuId);
        const subject = sku
          ? subjectPage.items.find((x) => x.id === sku.subjectId)
          : undefined;
        setSkuSubject({
          subjectId: subject?.id ?? null,
          subjectName: subject?.name ?? '该科目',
          fullScore: subject?.fullScore ?? 100,
        });
        // 预填该科目当前分数（student_subject_scores 行，无行则空）
        const current = (student?.subjectScores ?? []).find(
          (s) => s.subjectId === subject?.id,
        )?.score;
        form.setFieldValue('score', current ?? null);
      });
    }
  }, [open, item, form]);

  const consultants = staffs.filter(
    (s) =>
      s.status === 'ACTIVE' &&
      (s.campusGrants ?? []).some((g) => g.campusId === item.campusId),
  );

  const save = async () => {
    const values = await form.validateFields().catch(() => null);
    if (!values) return;
    setSaving(true);
    setError(null);
    try {
      await enrollmentsApi.updateEnrollmentSubmission(item.submissionId, {
        source: values.source,
        consultantId: values.consultantId ?? null,
        scores:
          values.score !== null && values.score !== undefined && skuSubject.subjectId
            ? { [skuSubject.subjectId]: values.score }
            : null,
        note: values.note ?? null,
      });
      message.success('报名单已更新（全部课程同步生效）');
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DraggableModal
      title={`编辑报名 · ${item.applicationId.slice(0, 8)}`}
      open={open}
      onCancel={onClose}
      onOk={save}
      okText="保存"
      confirmLoading={saving}
      destroyOnHidden
    >
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
      <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
        <Descriptions.Item label="学生">{item.studentName}</Descriptions.Item>
        <Descriptions.Item label="校区">{item.campusName}</Descriptions.Item>
        <Descriptions.Item label="课程SKU" span={2}>
          {item.skuName}
        </Descriptions.Item>
        <Descriptions.Item label="提交时间" span={2}>
          {fmtDateTime(item.submittedAt)}
        </Descriptions.Item>
      </Descriptions>
      <Form form={form} layout="vertical">
        <Form.Item label="报名来源" name="source" rules={[{ required: true }]}>
          <Select options={sources} />
        </Form.Item>
        <Form.Item label="顾问" name="consultantId">
          <Select
            allowClear
            placeholder="选择顾问（按报名校区在职员工）"
            options={consultants.map((s) => ({ value: s.id, label: s.name }))}
            data-testid="enrollment-edit-consultant"
          />
        </Form.Item>
        <Form.Item
          label={`科目分数 · ${skuSubject.subjectName}（用于筛选分班，满分 ${skuSubject.fullScore}）`}
          name="score"
          rules={[
            {
              type: 'integer',
              min: 0,
              max: skuSubject.fullScore,
              message: `分数必须为 0-${skuSubject.fullScore} 的整数`,
            },
          ]}
        >
          <InputNumber style={{ width: 120 }} placeholder={`0-${skuSubject.fullScore}`} />
        </Form.Item>
        <Form.Item label="备注" name="note">
          <Input.TextArea maxLength={500} showCount rows={2} />
        </Form.Item>
      </Form>
    </DraggableModal>
  );
}
